import { useTrainingDraft } from '../../hooks/useTrainingDraft';
// pages/training/TrainingQuizRunner.tsx
import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CircularProgress,
  Input,
  LinearProgress,
  Radio,
  RadioGroup,
  Sheet,
  Stack,
  Typography,
} from '@mui/joy';
import { ArrowLeft, ArrowRight, CheckCircle, Warning } from '@phosphor-icons/react';
import toast from 'react-hot-toast';
import { useMyTrainingQuiz, useSubmitMyTrainingQuiz, trainingErrorMessage } from '../../hooks/useTraining';
import type { MyTrainingQuizResult, MyTrainingRow, MyTrainingQuiz } from '../../types/training';

interface TrainingQuizRunnerProps {
  progressId: string;
  progress: MyTrainingRow;
  previewQuiz?: MyTrainingQuiz & { answerKey: Record<string, string> };
}

/**
 * One question at a time, because this is taken on a phone.
 *
 * The passing score, whether retakes are allowed and how many attempts are
 * left all live in HrSettings, which is HR-only — so the server is the
 * authority on whether an attempt is permitted and we show what it says.
 */
export const TrainingQuizRunner: React.FC<TrainingQuizRunnerProps> = ({ progressId, progress, previewQuiz }) => {
  const { data: loadedQuiz, isLoading, error } = useMyTrainingQuiz(progressId, !previewQuiz);
  const quiz = previewQuiz ?? loadedQuiz;
  const submitQuiz = useSubmitMyTrainingQuiz();

  const draft = useTrainingDraft<Record<string, string>>(progressId, 'quiz_draft', progress.draft?.quiz_draft || {}, !previewQuiz);
  const answers = draft.value;
  const setAnswers = draft.set;
  const [index, setIndex] = useState(0);
  const [result, setResult] = useState<MyTrainingQuizResult | null>(null);
  // Already graded before this visit — show the outcome, and the way back in,
  // rather than dropping the learner straight into question one.
  const [reviewing, setReviewing] = useState(
    progress.status === 'completed' || progress.status === 'failed'
  );

  if (isLoading && !previewQuiz) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert color="danger">{trainingErrorMessage(error, 'This quiz could not be loaded.')}</Alert>;
  }

  const questions = quiz?.questions ?? [];

  if (questions.length === 0) {
    return (
      <Alert color="warning" startDecorator={<Warning />}>
        This quiz has no questions yet, so it cannot be scored. Ask HR to finish setting it up.
      </Alert>
    );
  }

  if (reviewing || result) {
    const passed = result ? result.passed : progress.status === 'completed';
    const score = result ? result.score : progress.score;
    const attempts = result ? result.attempts : progress.attempts;
    const passingScore = result ? result.passing_score : quiz?.passing_score;

    return (
      <Stack spacing={2}>
        <Card variant="soft" color={passed ? 'success' : 'danger'} sx={{ p: { xs: 2, md: 3 }, alignItems: 'center' }}>
          {passed ? <CheckCircle size={40} /> : <Warning size={40} />}
          <Typography level="h2" sx={{ fontSize: { xs: '2rem', md: '2.5rem' } }}>
            {typeof score === 'number' ? `${score}%` : '—'}
          </Typography>
          <Typography level="title-md">{passed ? 'Passed' : 'Not a pass yet'}</Typography>
          <Typography level="body-sm">
            {typeof passingScore === 'number' ? `${passingScore}% needed to pass. ` : ''}
            {attempts} attempt{attempts === 1 ? '' : 's'} used.
          </Typography>
        </Card>
        {!passed && (
          <Button
            size="lg"
            onClick={() => {
              setAnswers({});
              setIndex(0);
              setResult(null);
              setReviewing(false);
            }}
          >
            Try again
          </Button>
        )}
      </Stack>
    );
  }

  const question = questions[index];
  const answered = Object.keys(answers).filter((id) => answers[id] !== undefined && answers[id] !== '').length;
  const isLast = index === questions.length - 1;
  const currentAnswer = answers[question.id] ?? '';

  const handleSubmit = async () => {
    try {
      if (previewQuiz) {
        const score = Math.round(questions.filter(q => answers[q.id] === previewQuiz.answerKey[q.id]).length / questions.length * 100);
        const passed = score >= previewQuiz.passing_score;
        setResult({ score, passed, attempts: 1, passing_score: previewQuiz.passing_score, status: passed ? 'completed' : 'failed', progress });
        return;
      }
      await draft.flush();
      const submitted = await submitQuiz.mutateAsync({ progressId, answers });
      setResult(submitted);
      toast[submitted.passed ? 'success' : 'error'](
        submitted.passed ? 'Passed — nice work' : 'Not a pass this time'
      );
    } catch (submitError) {
      toast.error(trainingErrorMessage(submitError, 'Your answers could not be submitted.'));
    }
  };

  return (
    <Stack spacing={2}>
      <Alert color={draft.error ? 'warning' : 'neutral'} endDecorator={draft.error ? <Button onClick={() => draft.flush().catch(() => undefined)}>Retry saving</Button> : undefined}>{draft.status}</Alert>
      <Box>
        <Typography level="body-xs" sx={{ mb: 0.5 }}>
          Question {index + 1} of {questions.length}
        </Typography>
        <LinearProgress
          determinate
          value={((index + 1) / questions.length) * 100}
          aria-label="Quiz progress"
        />
      </Box>

      <Typography level="title-lg">{question.question}</Typography>

      {question.options.length > 0 ? (
        <RadioGroup
          value={currentAnswer}
          onChange={(event) => setAnswers({ ...answers, [question.id]: event.target.value })}
        >
          <Stack spacing={1}>
            {question.options.map((option) => (
              <Sheet
                key={option}
                variant="outlined"
                sx={{
                  borderRadius: 'sm',
                  p: 1.5,
                  borderColor: currentAnswer === option ? 'primary.500' : undefined,
                }}
              >
                <Radio value={option} label={option} sx={{ width: '100%' }} />
              </Sheet>
            ))}
          </Stack>
        </RadioGroup>
      ) : (
        <Input
          placeholder="Your answer"
          value={currentAnswer}
          onChange={(event) => setAnswers({ ...answers, [question.id]: event.target.value })}
        />
      )}

      <Stack direction={{ xs: 'column-reverse', sm: 'row' }} spacing={1} sx={{ justifyContent: 'space-between' }}>
        <Button
          variant="outlined"
          color="neutral"
          startDecorator={<ArrowLeft />}
          disabled={index === 0}
          onClick={() => setIndex((current) => current - 1)}
        >
          Back
        </Button>
        {isLast ? (
          <Button
            size="lg"
            loading={submitQuiz.isPending}
            disabled={answered < questions.length}
            onClick={handleSubmit}
          >
            {answered < questions.length
              ? `Answer all ${questions.length} questions`
              : 'Submit answers'}
          </Button>
        ) : (
          <Button
            size="lg"
            endDecorator={<ArrowRight />}
            disabled={!currentAnswer}
            onClick={() => setIndex((current) => current + 1)}
          >
            Next
          </Button>
        )}
      </Stack>
    </Stack>
  );
};

export default TrainingQuizRunner;
